/** Leaf-level style primitives shared by badge-styles.ts, layout-styles.ts and layout-styles.detail.ts, extracted to break circular imports between those modules. */

import { FLEX_CENTER_STYLE, FLEX_COLUMN_STYLE, FLEX_ROW_CENTER_STYLE } from '@federa/const/style-flex-presets';
import { FULL_WIDTH_STYLE } from '@federa/const/style-size-presets';
import { colors } from '../css-refs.colors.ts';
import { divider, viewport } from '../css-refs.layout.ts';
import { typography } from '../css-refs.typography.ts';
import { spacing } from '../css-refs.spacing.ts';
import { borderRadius } from '../css-refs.effects.ts';
import type { CSSProperties } from 'react';

/** Interactive-state cursor tokens -- SSOT for positive-case `cursor` values; `disabled.cursor` in css-refs.interaction covers the negative case. */
export const cursor = {
	pointer: 'pointer',
	notAllowed: 'not-allowed',
	default: 'default',
	text: 'text',
	move: 'move',
	grab: 'grab',
	grabbing: 'grabbing',
	wait: 'wait',
	help: 'help',
} as const;

// THE GAP LADDER -- a `const/style-flex-presets` idiom base plus ONE `spacing` step, one export per rung, all seven
// rungs in THIS leaf (six based, plus the baseless {@link FLEX_ROW_GAP2_STYLE}, whose own docstring says why it has no
// base). The rungs cannot live beside their bases in `const/style-flex-presets.ts`: `spacing` is a
// `components/css-refs.spacing.ts` token read and the layer floor forbids a const-to-components edge, which is the
// same reason `const/style-grid-presets.ts` declines a gapped grid preset in its own header.
//
// THEY ARE THEREFORE GOVERNED BY NO MATCHER, and the fact is recorded rather than assumed:
// `QUAL-STYLE-FLEX-IDIOM-INLINE-001` derives its `PRESETS` index from the pure-literal const leaves alone (its record
// type admits STRING values, and `spacing[3]` is a token read, not a literal), and
// `QUAL-COMPONENT-STYLE-CONST-DUPLICATE-001` reaches this leaf but not these bodies.
//
// THAT SECOND CLAUSE WAS RE-MEASURED 2026-09-05 AND ITS GROUND CORRECTED, because the ground it used to give had gone
// stale under a peer widen and a stale ground is worse than none: it said the duplicate rule "discovers only
// `components/` leaves whose basename matches `STYLE_SSOT_BASENAME_RE` (`<topic>-styles.ts`), which this file's name
// does not." `STYLE_SSOT_BASENAME_SOURCE` gained a `styles?-<stem>` PREFIX arm, and `style-primitives` matches it --
// probed through the production helper rather than by reading the regex: `discoverStyleSsotPaths` returns 34 leaves
// and this file is one of them, and `STYLE_SSOT_PATH_RE` admits its repo-relative path. What actually keeps the rungs
// out is the CONSUMING rule's four-property cohort floor, which admits exactly 2 of this leaf's bodies
// (`TEXT_LG_BOLD_PRIMARY`, `PILL_BOX_STYLE`) -- every rung here is a two- or three-member object and sits under it.
// So the conclusion stands and the reason is now the true one. A rung re-spelled inline is caught by review, not by a
// scan -- a census, not a green scan, is what says the ladder is adopted.
//
// AND THE OBVIOUS CENSUS LIES, which is the part that had to be learned the expensive way. A rung census greps the gap
// member as `gap: spacing[2]`, and the SAME token read is also spelled with a QUOTED numeric key -- `spacing['2']`,
// which is the identical property. Three rung respellings sat unread behind that spelling until a census that
// normalized the quotes found them (2026-09-05, the `components/inputs/PhoneInput.*` family). Measured the same day:
// 95 quoted-index reads across 21 files, tree-wide. The dual spelling is a forbid-rule class of its own -- a quoted
// numeric index into `spacing` is never anything but its unquoted twin -- and until that rule exists, ANY census over
// a `spacing`-valued member must normalize the quotes before it may report zero.
//
// NO `FLEX_BETWEEN_GAP3_STYLE` RUNG, declined on a measurement 2026-09-05 and recorded here because this comment is
// where the ladder's admission bar lives. The exact two-member body `{ ...FLEX_BETWEEN_STYLE, gap: spacing[3] }` has
// FOUR carriers tree-wide, quote-normalized per the paragraph above: the brand app's `PaymentsPageContent` title row
// (moved there from `mfe/payments-payouts` payments-styles 2026-09-26), and the navigator transactions
// transactions-styles, StepHelpStrip and CreateBuyingModal.styles leaves. The bar every rung
// above cleared is SEVEN hand-spellings, and four does not reach it; ZERO of the four sits in `components/`, so the
// rung would ship into this leaf with no local consumer and one import hop added for all four. FALSIFIER: the exact
// body reaching seven carriers, or any carrier appearing in `components/`.

/** Column stack with a 1-step gap -- the tightest stacked pair: a label over its control, a title over its subtitle,
 *  a key over its value. RENAMED and RELOCATED here 2026-09-04 from `FIELD_STACK_STYLE` in `./layout-styles.ts`: the
 *  old name claimed the form-field role, and by the time a census read the whole frontend the SAME body was carrying a
 *  mission masthead, a properties hero text group, a profile section heading group and a title-report breakdown list
 *  at THIRTEEN further sites in four packages. The name described five of eighteen call sites, so it lied; the rung it
 *  actually occupies is what it is now named for, beside the rest of the ladder. */
export const FLEX_COLUMN_GAP1_STYLE: CSSProperties = {
	...FLEX_COLUMN_STYLE,
	gap: spacing[1],
};

/** Column stack with a 2-step gap -- the most-repeated stacked-layout decision across the frontend trees (form field
 *  stacks, list bodies, modal sections). Spread it and add per-site members rather than re-spelling the pair. */
export const FLEX_COLUMN_GAP2_STYLE: CSSProperties = {
	...FLEX_COLUMN_STYLE,
	gap: spacing[2],
};

/** Column stack with a 3-step gap -- the SECTION rung of the ladder: a panel's stacked blocks, a modal's body, a form's
 *  field groups. Named because a census measured it hand-spelled at EIGHT sites in four packages under seven local
 *  names, three of them the same word (`SECTION_STYLE` in `components/navigator`, `mfe/payments-payouts` and
 *  `spa/profile`), which is what a repeated decision with no home looks like. */
export const FLEX_COLUMN_GAP3_STYLE: CSSProperties = {
	...FLEX_COLUMN_STYLE,
	gap: spacing[3],
};

/** Column stack with a 4-step gap -- the PANEL rung: a form shell's sections, a detail panel's blocks. Measured at
 *  SEVEN hand-spellings in four packages. A separate decision from the 3-step rung above rather than drift: the two
 *  co-occur INSIDE one file (`spa/profile/frontend/profile-styles.ts` and `spa/mission`'s viewer styles each declare
 *  both), so the step is chosen per nesting depth and folding them would retune a layout, not a name. */
export const FLEX_COLUMN_GAP4_STYLE: CSSProperties = {
	...FLEX_COLUMN_STYLE,
	gap: spacing[4],
};

/** Centered flex row with a 2-step gap -- the ROW counterpart of `FLEX_COLUMN_GAP2_STYLE` above and the most-repeated
 *  inline row decision across the frontend trees: the admin top-bar action clusters, list-card badge rows and toolbar
 *  header rows. It was spelled longhand at nine sites in three packages, twice under the SAME local name
 *  (`TOPBAR_RIGHT_STYLE`, in `mfe/admin-finance` and `apps/admin-federa`) and twice in ONE file under two names
 *  (`HistoryPanel`'s `ENTRY_HEADER_LEFT` / `ENTRY_HEADER_RIGHT`), before it was one decision. Spread it and add
 *  per-site members rather than re-spelling the pair. */
export const FLEX_ROW_CENTER_GAP2_STYLE: CSSProperties = {
	...FLEX_ROW_CENTER_STYLE,
	gap: spacing[2],
};

/** Centered flex row with a 3-step gap -- the widest-spread row rung: a header's right-hand action group, a form's
 *  action bar, a hero's owner row, a phase header's two halves. Measured at ELEVEN hand-spellings in five packages,
 *  TWO of them inside one file (`page.progress.primitives.tsx`'s `phaseHeaderLeftStyle` / `phaseHeaderRightStyle`).
 *  It was already half-present here as the base of {@link FLEX_ROW_CENTER_GAP3_FULL_WIDTH_STYLE} below, which now
 *  composes it instead of re-spelling the pair. */
export const FLEX_ROW_CENTER_GAP3_STYLE: CSSProperties = {
	...FLEX_ROW_CENTER_STYLE,
	gap: spacing[3],
};

/** Plain flex ROW with a 2-step gap and NO cross-axis decision -- children keep the default `stretch`, which is the
 *  whole difference from {@link FLEX_ROW_CENTER_GAP2_STYLE} above: a submit/cancel action pair, a toggle-pill row and a
 *  search/clear toolbar all want their controls the SAME HEIGHT rather than each centered on its own text, so the
 *  `alignItems` the centered rung carries is not an omission here. Censused 2026-09-05 over `apps/`, `brand/`,
 *  `components/`, `mfe/` and `spa/` for the EXACT member set, quote-normalized (`spacing['2']` reads as `spacing[2]`):
 *  EIGHT hand-spellings, seven under local names (`ACTIONS`, ROW_GAP2, PILL_ROW_STYLE, EDIT_ACTIONS_ROW_STYLE,
 *  `TOP_BAR_ACTIONS`, BUTTON_ROW_STYLE, searchRow) and one inline in JSX. SEVEN of the eight adopt this rung; the
 *  eighth does not and the exception is worth its line -- `TOP_BAR_ACTIONS` had exactly one consumer, which always
 *  spread `alignItems: 'center'` over it, so the const and its delta together respelled
 *  {@link FLEX_ROW_CENTER_GAP2_STYLE} and that consumer reads the CENTERED rung instead.
 *
 *  IT IS THE ONE RUNG WITH NO `const/style-flex-presets` BASE, deliberately: the base would be a single-member
 *  `{ display: 'flex' }` preset, and registering that in `QUAL-STYLE-FLEX-IDIOM-INLINE-001`'s `PRESETS` record would
 *  generate a one-member run matching EVERY `display: 'flex'` in the tree. The pair is therefore spelled out here and,
 *  exactly like every other rung above, is governed by no matcher -- a census, not a green scan, says it stays adopted.
 *  The `spacing[1]` and `spacing[3]` steps of this same baseless family were measured the same day at THREE sites each
 *  and are NOT minted; the standing decline and its count live at `PAGINATION_INNER_STYLE` in
 *  `apps/admin-federa/src/app/transactions/page.list.tsx`. */
export const FLEX_ROW_GAP2_STYLE: CSSProperties = {
	display: 'flex',
	gap: spacing[2],
};

// COMPOSITES OVER THE LADDER -- each adds only the members that distinguish it, so a retune of a rung reaches them.

/** Wrapping flex row with a 2-step gap -- SSOT for the modal title row and the trigger-caller badge strip. */
export const FLEX_ROW_WRAP_GAP2_STYLE: CSSProperties = {
	...FLEX_ROW_CENTER_GAP2_STYLE,
	flexWrap: 'wrap',
};

/** Centered flex row with a 3-step gap that fills its container -- the CONTENT row of a full-width `Button`-wrapped
 *  list row, which is the shape `Button`'s single-child wrapper forces: the row's own box goes `display: block` and
 *  the flex that lays out icon/label/meta lives one level in, measuring against the full width. It was spelled
 *  longhand at SIX sites in four packages under five local names (`ROW_CONTENT_STYLE` twice, in `components/`
 *  notifications and `spa/explore`'s desktop filter bar; `WISHLIST_ITEM_ROW_STYLE`; `SUGGESTION_ROW_CONTENT_STYLE`;
 *  and inline inside two `MUTED_CONTROL_BUTTON_STYLE` compositions) before it was one decision. Spread it and add
 *  per-site members rather than re-spelling the run. */
export const FLEX_ROW_CENTER_GAP3_FULL_WIDTH_STYLE: CSSProperties = {
	...FLEX_ROW_CENTER_GAP3_STYLE,
	...FULL_WIDTH_STYLE,
};

/** Left-aligned column stack with a 3-step gap -- SSOT for a failure/error body block, which reads left-aligned rather
 *  than centered so the detail text stays scannable. */
export const FLEX_COLUMN_START_GAP3_STYLE: CSSProperties = {
	...FLEX_COLUMN_GAP3_STYLE,
	alignItems: 'flex-start',
};

/** The trailing column of a list card -- an end-aligned stack with a 1-step gap that never absorbs the row's slack,
 *  so the leading title/subtitle column takes it instead. Spelled out longhand at the admin users list, the finance
 *  accounts list and the ledger-corrections list under two different names before it was one decision. */
export const CARD_END_COLUMN_STYLE: CSSProperties = {
	...FLEX_COLUMN_GAP1_STYLE,
	alignItems: 'flex-end',
	flexShrink: 0,
};

/** Extra-small font + semibold weight typography preset (badge counts, data labels, compact emphasis). */
export const TEXT_XS_SEMIBOLD: CSSProperties = {
	fontSize: typography.fontSize.xs,
	fontWeight: typography.fontWeight.semibold,
};

export const BORDER_MUTED = `${divider.thickness.thin} solid ${colors.border.muted}`;

/** The DEFAULT-weight card border, sibling of {@link BORDER_MUTED}: the ONE declaration of this shorthand (a
 *  byte-identical twin lived in `./layout-styles.ts` until 2026-08-26, with consumers split across the two -- the
 *  respell class `QUAL-STYLE-DEFAULT-HAIRLINE-SSOT-001` now governs, exactly as its muted sibling rule governs
 *  {@link BORDER_MUTED}). One decision -- "how thick is a card edge" -- lives on `divider.thickness.thin` for both
 *  weights. */
export const BORDER_DEFAULT = `${divider.thickness.thin} solid ${colors.border.default}`;

/** Small font + medium weight typography preset (form labels, buttons, card labels). */
export const TEXT_SM_MEDIUM: CSSProperties = {
	fontSize: typography.fontSize.sm,
	fontWeight: typography.fontWeight.medium,
};

/** Large font + bold weight primary-text preset: the surface's most prominent line (an offer amount, an empty-state
 * title). One decision, so a retune of "how a headline reads" lands at every surface at once. */
export const TEXT_LG_BOLD_PRIMARY: CSSProperties = {
	fontSize: typography.fontSize.lg,
	fontWeight: typography.fontWeight.bold,
	letterSpacing: typography.letterSpacing.primary,
	color: colors.text.primary,
};

/**
 * The heading line of a panel, card or page section -- {@link TEXT_LG_BOLD_PRIMARY} flush against its container.
 *
 * It was spelled longhand at NINE sites in three MFEs under six different local names (`TITLE`,
 * `PANEL_TITLE_STYLE`, `HEADER_TITLE_STYLE`, `OWN_SECTION_TITLE_STYLE`, `TITLE_HEADING_STYLE`,
 * `BUY_PANEL_TITLE_STYLE`), and the copies had already drifted: seven omitted the `letterSpacing` the other two
 * carried. FIVE of the nine render inside navigator's SAME transactions flow, side by side, disagreeing with each
 * other -- which is the evidence that the omission was drift rather than a per-surface decision, so the fold
 * restores the tracking rather than dropping it.
 */
export const PANEL_TITLE_STYLE: CSSProperties = {
	...TEXT_LG_BOLD_PRIMARY,
	margin: 0,
};

/** {@link PANEL_TITLE_STYLE} at the semibold rung -- the lighter heading the navigator activity/transaction sections
 *  and noor's history panel wear. A SEPARATE decision from the bold rung, not a variant of it: the weight step is
 *  what distinguishes a sub-section heading from a panel heading, so the fold preserves each site's weight and cures
 *  only the tracking drift. */
export const PANEL_TITLE_SEMIBOLD_STYLE: CSSProperties = {
	...PANEL_TITLE_STYLE,
	fontWeight: typography.fontWeight.semibold,
};

export const MUTED_LABEL_STYLE: CSSProperties = {
	fontSize: typography.fontSize.xs,
	color: colors.text.tertiary,
};

/** A gate's notice -- its resolving spinner (`ViewportSpinner`, `components/feedback/ViewportSpinner.tsx`) or its
 *  not-authorized sentence -- centred in the viewport the gated surface would have filled. One frame for every gate. */
export const VIEWPORT_NOTICE_STYLE: CSSProperties = {
	...FLEX_CENTER_STYLE,
	minHeight: viewport.minHeight,
	padding: spacing[6],
};

/** Compact pill box preset (xl radius, 3/2 padding, xs font) -- the base both the error alert pill and neutral loading pill compose. */
export const PILL_BOX_STYLE: CSSProperties = {
	borderRadius: borderRadius.xl,
	paddingLeft: spacing[3],
	paddingRight: spacing[3],
	paddingTop: spacing[2],
	paddingBottom: spacing[2],
	fontSize: typography.fontSize.xs,
};

/** Tabular-figure preset for fixed-width digits -- SSOT for the `fontVariantNumeric: 'tabular-nums'` repeated across number-aligned displays. */
export const TABULAR_NUMS_STYLE: CSSProperties = {
	fontVariantNumeric: 'tabular-nums',
};

/** Canonical unstyled `<ul>`/`<ol>` reset (no bullets, no margin, no padding) -- SSOT for the three-property reset duplicated across dropdowns, pagination and modal lists. */
export const UL_RESET_STYLE: CSSProperties = {
	listStyle: 'none',
	margin: 0,
	padding: 0,
};

/** Underlined text-link base style -- SSOT for the `textDecoration: 'underline'` shared by link buttons, footer back links and error detail links. */
export const TEXT_LINK_BASE_STYLE: CSSProperties = {
	textDecoration: 'underline',
};

// The centred de-emphasised status line left this leaf 2026-09-05 as `TEXT_SM_TERTIARY_CENTERED` in `./layout-styles.ts`.
// Under its old name (`NOTIFICATIONS_EMPTY_STATE_STYLE`) it claimed the notifications role while a wizard step and an
// mfe LOADING line carried the same body; and it belongs beside `TEXT_SM_TERTIARY`, whose two members it had to respell
// longhand while it lived here (this leaf breaks the badge-styles/layout-styles circular import, so it cannot import
// back). No consumer of this leaf's circular-break contract ever imported it.

/** CSS multi-line ellipsis style: the four properties required for `-webkit-box` line-clamping to N lines. */
export function getLineClampStyles(lineClamp: number): CSSProperties {
	return {
		display: '-webkit-box',
		WebkitLineClamp: lineClamp,
		WebkitBoxOrient: 'vertical',
		overflow: 'hidden',
	};
}
